import moment from 'moment';
import { getFieldLabel, objectToString } from '../components/Eligibility/eligibilityAgentUtils';

export const AUDIT_LOG_ITEM_KIND = Object.freeze({
  VERIFICATION: 'verification',
  AGENT_EVENT: 'agentEvent',
});

const EVENT_TYPE_LABELS = {
  auto_biller_verify_withheld: 'Pre-Biller Verified Withheld',
};

const formatDate = iso => moment(iso).format('MM/DD/YYYY [at] HH:mm');

const byCreatedAtDesc = (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();

const humanize = key =>
  key
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

// --- Insurance verification decisions -------------------------------------------------------------

const verificationUserName = user => (user ? `${user.firstName} ${user.lastName}` : 'Unknown user');

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

const changeLabel = (key, resource, before, after) =>
  `${resource ? `${capitalize(resource)}: ` : ''}${getFieldLabel(key)}: ${objectToString(before)} → ${objectToString(after)}`;

const VERIFICATION_ACTION_LABELS = {
  accepted: 'Accepted',
  rejected: 'Rejected',
};

const mapVerificationToItem = (verification, index, total) => {
  const isRejected = verification.status === 'rejected';
  const action = VERIFICATION_ACTION_LABELS[verification.status] || verification.status;
  const subtext = isRejected
    ? `Reason: ${verification.rejectReason || 'Not provided'}`
    : `Insurance Verified on ${verification.verifiedAt ? formatDate(verification.verifiedAt) : 'N/A'} by ${
        verification.verifiedByUser ? verificationUserName(verification.verifiedByUser) : 'Unknown user'
      }`;

  return {
    kind: AUDIT_LOG_ITEM_KIND.VERIFICATION,
    id: verification.id,
    createdAt: verification.createdAt,
    status: verification.status,
    header: `#${total - index} Changes ${action} on ${formatDate(verification.createdAt)} by ${verificationUserName(
      verification.creator,
    )}`,
    subtext,
    changes: verification.changes.map(({ key, resource, before, after }) => ({
      label: changeLabel(key, resource, before, after),
    })),
  };
};

// --- Eligibility agent events ---------------------------------------------------------------------

const eventActor = creator => `${creator?.firstName ?? ''} ${creator?.lastName ?? ''}`.trim() || 'Unknown user';

const eventTypeLabel = type => EVENT_TYPE_LABELS[type] || humanize(type || 'eligibility_agent_event');

const mapEventToItem = event => ({
  kind: AUDIT_LOG_ITEM_KIND.AGENT_EVENT,
  id: event.id,
  createdAt: event.createdAt,
  type: event.type,
  header: `${eventTypeLabel(event.type)} on ${formatDate(event.createdAt)} by ${eventActor(event.creator)}`,
  message: event.message || '',
  details: event.details ?? [],
});

/**
 * @param {{insuranceVerifications?: Array<Object>, eligibilityAgentEvents?: Array<Object>}} response
 * @returns {Array<Object>} verification and agent event items, each tagged with its `kind`
 */
export const parseAuditLogTimeline = ({ insuranceVerifications = [], eligibilityAgentEvents = [] } = {}) => {
  const verifications = [...insuranceVerifications].sort(byCreatedAtDesc);

  return [
    ...verifications.map((verification, index) => mapVerificationToItem(verification, index, verifications.length)),
    ...eligibilityAgentEvents.map(mapEventToItem),
  ].sort(byCreatedAtDesc);
};
